import { Button, Loading, notify, useConfirmModal } from '@affine/component';
import { useGuard } from '@affine/core/components/guard';
import {
  AuthService,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { DocService } from '@affine/core/modules/doc';
import {
  FolderApiService,
  type GroupData,
} from '@affine/core/modules/folder-api';
import { ObjectPermissionMutationService } from '@affine/core/modules/permissions';
import { PermissionRoleMenu } from '@affine/core/modules/permissions/components/permission-role-menu';
import {
  createObjectPermissionRoleOptions,
  getObjectPermissionAreaBlockedNotice,
  isDocGroupRolePartiallyLimited,
  objectPermissionRoleLabel,
  useObjectPermissionOnlineOnlyState,
} from '@affine/core/modules/permissions/utils/object-permission-ui';
import { withPermissionRequestTimeout } from '@affine/core/modules/permissions/utils/permission-request';
import * as styles from '@affine/core/modules/permissions/views/permission-access-list.css';
import { PermissionGroupRow } from '@affine/core/modules/permissions/views/permission-invite-picker';
import { PermissionReviewList } from '@affine/core/modules/permissions/views/permission-review-list';
import * as reviewStyles from '@affine/core/modules/permissions/views/permission-review-list.css';
import { isAppDataChangeCancelled } from '@affine/core/modules/streamlain/native-app';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  DocRole,
  type GetDocGroupRolesQuery,
  getDocGroupRolesQuery,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import * as panelStyles from './doc-access-panel.css';
import { DocInheritedAccessPanel } from './doc-inherited-access-panel';
import { DOC_ASSIGNABLE_ROLES, DocPeoplePanel } from './doc-people-section';

type GroupRole = GetDocGroupRolesQuery['docGroupRoles'][number];
interface GroupDataSnapshot {
  groups: GroupData[];
  roles: GroupRole[];
}
const cache = new Map<string, GroupDataSnapshot>();
const PAGE_SIZE = 24;

const ACCESS_TABS = ['people', 'groups', 'inherited'] as const;
type AccessTab = (typeof ACCESS_TABS)[number];

/**
 * Who has access to the document: one tile per access kind shows its count
 * and selects the list below. All lists load on open so every count is known;
 * each list stays bounded to one page.
 */
export function DocPermissionSections({
  disabled = false,
}: {
  disabled?: boolean;
}) {
  const t = useI18n();
  const id = useId();
  const [active, setActive] = useState<AccessTab>('people');
  const [totals, setTotals] = useState<Record<AccessTab, number | null>>({
    people: null,
    groups: null,
    inherited: null,
  });
  const tabRefs = useRef(new Map<AccessTab, HTMLButtonElement>());
  const panelRefs = useRef(new Map<AccessTab, HTMLDivElement>());
  const setPeopleTotal = useCallback(
    (total: number | null) =>
      setTotals(current => ({ ...current, people: total })),
    []
  );
  const setGroupsTotal = useCallback(
    (total: number | null) =>
      setTotals(current => ({ ...current, groups: total })),
    []
  );
  const setInheritedTotal = useCallback(
    (total: number | null) =>
      setTotals(current => ({ ...current, inherited: total })),
    []
  );
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const panel = panelRefs.current.get(active);
    if (!panel) return;
    const animation = streamLainAnimationController.play(
      panel,
      [
        { opacity: 0, transform: 'translateY(4px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      'enter'
    );
    return () => animation.cancel();
  }, [active]);
  const labels: Record<AccessTab, { label: string; hint: string }> = {
    people: {
      label: t['com.streamlain.folder-share.people'](),
      hint: t['com.streamlain.share-menu.access.direct'](),
    },
    groups: {
      label: t['com.streamlain.folder-share.groups'](),
      hint: t['com.streamlain.share-menu.access.direct'](),
    },
    inherited: {
      label: t['com.streamlain.share-menu.access.inherited'](),
      hint: t['com.streamlain.share-menu.access.via-folders'](),
    },
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step =
      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    const index = ACCESS_TABS.indexOf(active);
    const next =
      event.key === 'Home'
        ? ACCESS_TABS[0]
        : event.key === 'End'
          ? ACCESS_TABS[ACCESS_TABS.length - 1]
          : step
            ? ACCESS_TABS[
                (index + step + ACCESS_TABS.length) % ACCESS_TABS.length
              ]
            : null;
    if (!next) return;
    event.preventDefault();
    setActive(next);
    tabRefs.current.get(next)?.focus();
  };
  const panel = (tab: AccessTab) => {
    if (tab === 'people')
      return <DocPeoplePanel disabled={disabled} onTotal={setPeopleTotal} />;
    if (tab === 'groups')
      return <DocGroupsPanel disabled={disabled} onTotal={setGroupsTotal} />;
    return <DocInheritedAccessPanel onTotal={setInheritedTotal} />;
  };
  return (
    <section className={panelStyles.root} data-testid="doc-permission-access">
      <div className={panelStyles.heading} id={`${id}-heading`}>
        {t['com.streamlain.share-menu.access.title']()}
      </div>
      <div
        role="tablist"
        aria-labelledby={`${id}-heading`}
        className={panelStyles.tabs}
      >
        {ACCESS_TABS.map(tab => (
          <button
            key={tab}
            ref={node => {
              if (node) tabRefs.current.set(tab, node);
              else tabRefs.current.delete(tab);
            }}
            type="button"
            role="tab"
            id={`${id}-${tab}-tab`}
            aria-selected={active === tab}
            aria-controls={`${id}-${tab}-panel`}
            tabIndex={active === tab ? 0 : -1}
            className={panelStyles.tab}
            data-testid={`doc-access-tab-${tab}`}
            onClick={() => setActive(tab)}
            onKeyDown={onKeyDown}
          >
            <span className={panelStyles.tabCount}>
              {totals[tab] === null ? <Loading size={14} /> : totals[tab]}
            </span>
            <span className={panelStyles.tabLabel}>{labels[tab].label}</span>
            <span className={panelStyles.tabHint}>{labels[tab].hint}</span>
          </button>
        ))}
      </div>
      {ACCESS_TABS.map(tab => (
        <div
          key={tab}
          ref={node => {
            if (node) panelRefs.current.set(tab, node);
            else panelRefs.current.delete(tab);
          }}
          role="tabpanel"
          id={`${id}-${tab}-panel`}
          aria-labelledby={`${id}-${tab}-tab`}
          hidden={active !== tab}
          className={panelStyles.panel}
          data-testid={
            tab === 'people'
              ? 'doc-permission-people'
              : `doc-permission-${tab}-panel`
          }
        >
          {panel(tab)}
        </div>
      ))}
    </section>
  );
}

/** Direct group grants of the document; searching also offers groups to add. */
function DocGroupsPanel({
  disabled,
  onTotal,
}: {
  disabled: boolean;
  onTotal(total: number | null): void;
}) {
  const t = useI18n();
  const doc = useService(DocService).doc;
  const workspace = useService(WorkspaceService).workspace;
  const account = useLiveData(useService(AuthService).session.account$);
  const server = useService(WorkspaceServerService).server;
  const folders = useService(FolderApiService);
  const mutations = useService(ObjectPermissionMutationService);
  const { openConfirmModal } = useConfirmModal();
  const online = useObjectPermissionOnlineOnlyState({
    backendUnavailable: !server,
  });
  const canManage = useGuard('Doc_Users_Manage', doc.id);
  const key = `${server?.baseUrl}:${account?.id}:${workspace.id}:${doc.id}`;
  const [snapshot, setSnapshot] = useState<{
    key: string;
    data: GroupDataSnapshot;
  }>();
  const data = snapshot?.key === key ? snapshot.data : cache.get(key);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pending, setPending] = useState(false);
  const mutationBusy = useRef(false);
  const request = useRef<AbortController | null>(null);
  const load = useCallback(async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setError(false);
    if (!server || online.offline) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [result, groups] = await withPermissionRequestTimeout(
        Promise.all([
          server.gql({
            query: getDocGroupRolesQuery,
            variables: { workspaceId: workspace.id, docId: doc.id },
            context: {
              signal: AbortSignal.any([
                controller.signal,
                AbortSignal.timeout(15_000),
              ]),
            },
          }),
          folders.getGroups(workspace.id),
        ])
      );
      if (controller.signal.aborted) return;
      const next = { groups, roles: result.docGroupRoles ?? [] };
      cache.delete(key);
      cache.set(key, next);
      while (cache.size > 64) cache.delete(cache.keys().next().value!);
      setSnapshot({ key, data: next });
    } catch {
      if (!controller.signal.aborted) setError(true);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [doc.id, folders, key, online.offline, server, workspace.id]);
  useEffect(() => {
    void load();
    return () => request.current?.abort();
  }, [load]);
  const total = data ? data.roles.length : null;
  useEffect(() => {
    if (total !== null) onTotal(total);
  }, [onTotal, total]);
  const locked = disabled || online.disabled || pending || loading || error;
  const roles = createObjectPermissionRoleOptions(t, DOC_ASSIGNABLE_ROLES);
  const roleById = new Map(data?.roles.map(role => [role.groupId, role]));
  const groupById = new Map(data?.groups.map(group => [group.id, group]));
  const query = search.trim().toLocaleLowerCase();
  const matches = (name: string) =>
    !query || name.toLocaleLowerCase().includes(query);
  const existing = (data?.roles ?? [])
    .filter(role => matches(groupById.get(role.groupId)?.name ?? role.groupId))
    .map(role => ({
      id: role.groupId,
      name: groupById.get(role.groupId)?.name ?? role.groupId,
      role,
    }));
  const candidates =
    canManage && query
      ? (data?.groups ?? [])
          .filter(group => !roleById.has(group.id) && matches(group.name))
          .map(group => ({ id: group.id, name: group.name, role: undefined }))
      : [];
  const rows = [...existing, ...candidates];
  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1)
  );
  const visible = rows.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE
  );
  const change = async (
    id: string,
    action: 'grant' | 'update' | 'revoke',
    role?: DocRole
  ) => {
    if (locked || mutationBusy.current) return;
    mutationBusy.current = true;
    setPending(true);
    try {
      await mutations.execute({
        action,
        principal: { id, kind: 'group' },
        role,
        target: { id: doc.id, kind: 'doc' },
      });
      await load();
    } catch (error) {
      if (!isAppDataChangeCancelled(error))
        notify.error({
          title:
            t['com.streamlain.share-menu.doc-permissions.mutation-failed'](),
        });
    } finally {
      mutationBusy.current = false;
      setPending(false);
    }
  };
  return (
    <PermissionReviewList
      testId="doc-permission-group"
      search={search}
      onSearchChange={value => {
        setSearch(value);
        setPage(0);
      }}
      placeholder={t['com.streamlain.permissions.review.search-groups']()}
      loading={loading}
      error={error}
      offline={online.offline}
      onRetry={() => void load()}
      total={data ? rows.length : null}
      start={currentPage * PAGE_SIZE}
      count={visible.length}
      onPrevious={currentPage > 0 ? () => setPage(currentPage - 1) : undefined}
      onNext={
        (currentPage + 1) * PAGE_SIZE < rows.length
          ? () => setPage(currentPage + 1)
          : undefined
      }
    >
      {visible.map(row => {
        const notice = row.role
          ? getObjectPermissionAreaBlockedNotice(t, row.role)
          : null;
        const limited =
          row.role &&
          isDocGroupRolePartiallyLimited(
            row.role.role,
            groupById.get(row.id)?.memberWorkspaceRoles ?? []
          );
        return (
          <div key={row.id} className={styles.accessRowFrame}>
            <PermissionGroupRow
              groupName={row.name}
              roleSlot={
                row.role ? (
                  canManage ? (
                    <PermissionRoleMenu
                      role={row.role.role}
                      options={roles}
                      disabled={locked}
                      onSelect={role => void change(row.id, 'update', role)}
                    />
                  ) : (
                    <span className={styles.roleLabel}>
                      {objectPermissionRoleLabel(t, row.role.role)}
                    </span>
                  )
                ) : (
                  <AddGroupActions
                    name={row.name}
                    disabled={locked}
                    onAdd={role => change(row.id, 'grant', role)}
                  />
                )
              }
              onRemove={
                canManage && row.role
                  ? () =>
                      openConfirmModal({
                        title:
                          t['com.streamlain.folder-share.remove-group-title'](),
                        description: t[
                          'com.streamlain.share-menu.doc-permissions.remove-group-description'
                        ]({ name: row.name }),
                        confirmText: t['Remove'](),
                        cancelText: t['Cancel'](),
                        confirmButtonOptions: { variant: 'error' },
                        onConfirm: () => change(row.id, 'revoke'),
                      })
                  : undefined
              }
              removeDisabled={locked}
            />
            {notice || limited ? (
              <div className={styles.areaBlockedNotice}>
                {notice ??
                  t[
                    'com.streamlain.permissions.group-role-partially-limited'
                  ]()}
              </div>
            ) : null}
          </div>
        );
      })}
    </PermissionReviewList>
  );
}

function AddGroupActions({
  name,
  disabled,
  onAdd,
}: {
  name: string;
  disabled: boolean;
  onAdd(role: DocRole): Promise<void>;
}) {
  const t = useI18n();
  const [role, setRole] = useState<DocRole>(DocRole.Editor);
  return (
    <div className={reviewStyles.actions}>
      <PermissionRoleMenu
        role={role}
        options={createObjectPermissionRoleOptions(t, DOC_ASSIGNABLE_ROLES)}
        disabled={disabled}
        onSelect={setRole}
      />
      <Button
        variant="plain"
        disabled={disabled}
        aria-label={`${t['com.streamlain.permissions.review.add']()} ${name}`}
        onClick={() => void onAdd(role)}
      >
        {t['com.streamlain.permissions.review.add']()}
      </Button>
    </div>
  );
}
